import React, {
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Box, Link, Popover, Typography } from '@mui/material';

import Icon from '../IconSvg/Icon';
import {
  getToggleTipPaperSx,
  toggleTipBodySx,
  toggleTipCloseButtonSx,
  toggleTipHeaderSx,
  toggleTipLinkSx,
  type ToggleTipPosition,
} from './toggleTip.styles';
import {
  TOGGLE_TIP_Z_INDEX,
  positionToggleTipPanel,
  resolveToggleTipIcon,
} from './toggleTipUtils';
import { lelyTokenPrimitives } from '@/common/styles/newStyles/LelyMuiTheme';
import { LelyButton } from '@/common/components';

export type ToggleTipProps = {
  headerText?: string;
  body: React.ReactElement | string;
  position?: ToggleTipPosition;
  chevron?: boolean;
  containsLink?: boolean;
  children: React.ReactElement;
  link?: string;
  linkText?: string;
};

let dismissActive: (() => void) | undefined;

function ToggleTip({
  headerText,
  body,
  position = 'top',
  chevron = true,
  containsLink = false,
  children,
  link = '#',
  linkText = 'Link',
}: ToggleTipProps) {
  const tooltipId = useId();
  const iconRef = useRef<Element | null>(null);
  const paperRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [positioned, setPositioned] = useState(false);
  const hasHeader = Boolean(headerText);

  const handleClose = useCallback(() => {
    setOpen(false);
    setPositioned(false);
    iconRef.current = null;
    if (dismissActive === handleClose) {
      dismissActive = undefined;
    }
  }, []);

  const updatePosition = useCallback(() => {
    const paper = paperRef.current;
    const icon = iconRef.current;
    if (!paper || !icon) {
      return false;
    }

    if (paper.offsetWidth === 0) {
      return false;
    }

    positionToggleTipPanel(paper, icon, position, chevron);
    setPositioned(true);
    return true;
  }, [chevron, position]);

  const schedulePosition = useCallback(() => {
    let rafId = 0;
    let attempts = 0;

    const tryPosition = () => {
      if (updatePosition() || attempts++ > 60) {
        return;
      }

      rafId = requestAnimationFrame(tryPosition);
    };

    tryPosition();

    return () => cancelAnimationFrame(rafId);
  }, [updatePosition]);

  const handleToggle = useCallback(
    (event: React.SyntheticEvent<HTMLElement>) => {
      children.props.onClick?.(event);

      if (open) {
        handleClose();
        return;
      }

      dismissActive?.();
      dismissActive = handleClose;
      iconRef.current = resolveToggleTipIcon(
        event.currentTarget as HTMLElement,
      );
      setPositioned(false);
      setOpen(true);
    },
    [children, handleClose, open],
  );

  useLayoutEffect(() => {
    if (!open) {
      return;
    }

    const cancelPosition = schedulePosition();
    const onResize = () => {
      updatePosition();
    };

    window.addEventListener('resize', onResize);
    return () => {
      cancelPosition();
      window.removeEventListener('resize', onResize);
    };
  }, [open, schedulePosition, updatePosition]);

  const trigger = React.cloneElement(children, {
    onClick: handleToggle,
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
      children.props.onKeyDown?.(event);
      if (
        !event.defaultPrevented &&
        (event.key === 'Enter' || event.key === ' ')
      ) {
        event.preventDefault();
        handleToggle(event);
      }
    },
    role: children.props.role ?? 'button',
    tabIndex: children.props.tabIndex ?? 0,
    'aria-describedby': open ? tooltipId : undefined,
    'aria-expanded': open,
    style: {
      cursor: 'pointer',
      display: 'inline-flex',
      ...children.props.style,
    },
  });

  return (
    <>
      {trigger}
      <Popover
        open={open}
        anchorReference="none"
        onClose={handleClose}
        disableEnforceFocus={containsLink}
        TransitionProps={{ timeout: 0, onEntered: updatePosition }}
        slotProps={{
          root: { sx: { zIndex: TOGGLE_TIP_Z_INDEX } },
          paper: {
            ref: paperRef,
            id: tooltipId,
            role: 'tooltip',
            'aria-hidden': !open,
            tabIndex: containsLink ? 0 : undefined,
            sx: (theme) => ({
              ...getToggleTipPaperSx(theme, position, chevron),
              ...(!positioned && { top: -10000, left: -10000 }),
            }),
          },
        }}
      >
        <Box data-testid="toggle-tip">
          <Box display="flex" justifyContent="center">
            {hasHeader && (
              <Typography
                variant="md"
                sx={(theme) => ({
                  ...toggleTipHeaderSx,
                  fontWeight: 'bold',
                  marginBottom: theme.lelySpacing.MD,
                })}
              >
                {headerText}
              </Typography>
            )}
            <LelyButton
              variant="tertiary"
              inverted
              aria-label="close toggle-tip"
              onClick={handleClose}
              sx={toggleTipCloseButtonSx}
              data-testid="toggle-tip-close-button"
              leadingIcon="lely-icon-close"
            />
          </Box>

          {typeof body === 'string' ? (
            <Typography component="div" variant="body2" sx={toggleTipBodySx}>
              {body}
            </Typography>
          ) : (
            body
          )}

          {containsLink && (
            <Link
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              sx={toggleTipLinkSx}
            >
              <span>{linkText}</span>
              <Icon
                name="lely-icon-caret-right"
                style={{
                  width: lelyTokenPrimitives.spaceXl,
                  height: lelyTokenPrimitives.spaceXl,
                }}
              />
            </Link>
          )}
        </Box>
      </Popover>
    </>
  );
}

export default ToggleTip;
